// live-calendar.js
class LiveCalendar {
  constructor() {
    this.eventsData = window.liveCalendarData || [];
    this.currentDate = new Date();
    this.calendarContainer = document.querySelector('.calendar-card');
    this.eventsListContainer = document.querySelector('.upcoming-events-list');
    this.init();
  }

  init() {
    // Parse and format events data
    this.formatEventsData();
    
    // Update calendar display
    this.updateCalendar();
    
    // Update events list
    this.updateEventsList();
    
    // Add navigation controls if needed
    this.addCalendarControls();
  }

  formatEventsData() {
    this.eventsData = this.eventsData.map(event => {
      const eventDate = new Date(event.date_time);
      
      // Format for display
      return {
        ...event,
        dateObject: eventDate,
        formattedDate: this.formatDate(eventDate),
        timeJST: this.convertToJST(eventDate),
        timeJST12h: this.format12HourTime(eventDate),
        dayKey: this.getDayKey(eventDate)
      };
    }).sort((a, b) => a.dateObject - b.dateObject);
  }

  formatDate(date) {
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  convertToJST(date) {
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    const jstDate = new Date(utcDate.getTime() + 9 * 3600000);
    return jstDate.toTimeString().slice(0, 5);
  }

  format12HourTime(date) {
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12
    return `${hours}:${minutes} ${ampm}`;
  }

  getDayKey(date) {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  }

  updateCalendar() {
    if (!this.calendarContainer) return;

    // Get events grouped by day
    const eventsByDay = {};
    this.eventsData.forEach(event => {
      const dayKey = event.dayKey;
      if (!eventsByDay[dayKey]) {
        eventsByDay[dayKey] = [];
      }
      eventsByDay[dayKey].push(event);
    });

    // Add data attributes to calendar cells
    const calendarCells = this.calendarContainer.querySelectorAll('td:not(.empty)');
    calendarCells.forEach(cell => {
      const dayNumber = cell.textContent.trim();
      if (dayNumber) {
        const dayKey = this.getCurrentMonthDayKey(parseInt(dayNumber));
        const dayEvents = eventsByDay[dayKey] || [];
        
        if (dayEvents.length > 0) {
          cell.dataset.events = JSON.stringify(dayEvents);
          this.addEventDots(cell, dayEvents);
        }
      }
    });

    // Add click handlers
    this.addCalendarEventHandlers();
  }

  getCurrentMonthDayKey(day) {
    const year = this.currentDate.getFullYear();
    const month = (this.currentDate.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  }

  addEventDots(cell, events) {
    // Clear existing dots
    const existingDots = cell.querySelector('.event-dots');
    if (existingDots) existingDots.remove();

    // Create dots container
    const dotsContainer = document.createElement('div');
    dotsContainer.className = 'event-dots';
    dotsContainer.style.display = 'flex';
    dotsContainer.style.justifyContent = 'center';
    dotsContainer.style.gap = '2px';
    dotsContainer.style.marginTop = '3px';
    dotsContainer.style.flexWrap = 'wrap';

    // Add dots for each event
    events.forEach(event => {
      const dot = document.createElement('span');
      dot.className = `cal-dot dot-${this.getPlatformClass(event.plateform)}`;
      dot.title = `${event.plateform} Live - ${event.timeJST12h} JST`;
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        this.showEventDetails(event);
      });
      dotsContainer.appendChild(dot);
    });

    cell.appendChild(dotsContainer);
  }

  getPlatformClass(platform) {
    const platformLower = platform.toLowerCase();
    if (platformLower.includes('instagram') || platformLower.includes('insta')) return 'insta';
    if (platformLower.includes('tiktok')) return 'tiktok';
    if (platformLower.includes('facebook') || platformLower.includes('fb')) return 'facebook';
    if (platformLower.includes('youtube') || platformLower.includes('yt')) return 'youtube';
    return 'other';
  }

  updateEventsList() {
    if (!this.eventsListContainer) return;

    // Clear existing list
    this.eventsListContainer.innerHTML = '';

    // Get upcoming events
    const now = new Date();
    const upcomingEvents = this.eventsData.filter(event => event.dateObject > now);

    // Display up to 4 events
    const eventsToShow = upcomingEvents.slice(0, 4);
    
    eventsToShow.forEach(event => {
      const listItem = document.createElement('li');
      listItem.className = 'upcoming-event-item';
      
      const platformClass = this.getPlatformClass(event.plateform);
      
      listItem.innerHTML = `
        <div>
          ${event.formattedDate} — <b>${event.plateform} Live</b> — ${event.timeJST12h} JST
        </div>
      `;
      
      listItem.addEventListener('click', () => this.showEventDetails(event));
      
      this.eventsListContainer.appendChild(listItem);
    });

    // Show "more events" indicator
    if (upcomingEvents.length > 4) {
      const moreIndicator = document.createElement('p');
      moreIndicator.style.marginTop = '12px';
      moreIndicator.style.fontSize = '12px';
      moreIndicator.style.color = '#6b7280';
      moreIndicator.textContent = `+${upcomingEvents.length - 4} more events this month`;
      this.eventsListContainer.parentNode.appendChild(moreIndicator);
    }
  }

  showEventDetails(event) {
    // Create modal or tooltip with event details
    const modal = document.createElement('div');
    modal.className = 'event-modal';
    modal.innerHTML = `
      <div class="event-modal-content">
        <h3>${event.plateform} Live</h3>
        <p><strong>Date:</strong> ${event.formattedDate}</p>
        <p><strong>Time:</strong> ${event.timeJST12h} JST</p>
        ${event.description ? `<p><strong>Description:</strong> ${event.description}</p>` : ''}
        ${event.link ? `<p><a href="${event.link}" target="_blank">Join Live</a></p>` : ''}
        <button class="close-modal">Close</button>
      </div>
    `;
    
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    `;
    
    modal.querySelector('.close-modal').addEventListener('click', () => {
      document.body.removeChild(modal);
    });
    
    document.body.appendChild(modal);
  }

  addCalendarEventHandlers() {
    const cells = this.calendarContainer.querySelectorAll('td:not(.empty)');
    cells.forEach(cell => {
      cell.addEventListener('click', () => {
        const eventsData = cell.dataset.events;
        if (eventsData) {
          const events = JSON.parse(eventsData);
          this.showDayEvents(events, cell);
        }
      });
    });
  }

  showDayEvents(events, cell) {
    // Show events for the clicked day
    const tooltip = document.createElement('div');
    tooltip.className = 'day-events-tooltip';
    
    let eventsHTML = events.map(event => `
      <div class="day-event-item">
        <span class="dot dot-${this.getPlatformClass(event.plateform)}"></span>
        ${event.plateform} Live at ${event.timeJST12h}
      </div>
    `).join('');
    
    tooltip.innerHTML = `<div class="tooltip-content">${eventsHTML}</div>`;
    
    // Position and show tooltip
    const rect = cell.getBoundingClientRect();
    tooltip.style.cssText = `
      position: absolute;
      top: ${rect.bottom + 5}px;
      left: ${rect.left}px;
      background: white;
      border: 1px solid #ddd;
      padding: 10px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      z-index: 100;
      min-width: 200px;
    `;
    
    document.body.appendChild(tooltip);
    
    // Close on click outside
    setTimeout(() => {
      const closeTooltip = (e) => {
        if (!tooltip.contains(e.target) && !cell.contains(e.target)) {
          document.body.removeChild(tooltip);
          document.removeEventListener('click', closeTooltip);
        }
      };
      document.addEventListener('click', closeTooltip);
    }, 100);
  }

  addCalendarControls() {
    // Add month navigation if needed
    const calHeader = this.calendarContainer.querySelector('.cal-header');
    if (calHeader) {
      const navContainer = document.createElement('div');
      navContainer.className = 'calendar-nav';
    //   navContainer.innerHTML = `
    //     <button class="prev-month">&larr;</button>
    //     <span class="current-month"></span>
    //     <button class="next-month">&rarr;</button>
    //   `;
      
      calHeader.parentNode.insertBefore(navContainer, calHeader.nextSibling);
      
      // Add navigation handlers
      navContainer.querySelector('.prev-month').addEventListener('click', () => {
        this.navigateMonth(-1);
      });
      
      navContainer.querySelector('.next-month').addEventListener('click', () => {
        this.navigateMonth(1);
      });
    }
  }

  navigateMonth(direction) {
    // Implementation for month navigation
    console.log('Navigate month:', direction);
    // You would need to fetch new events for the new month
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  if (window.liveCalendarData) {
    new LiveCalendar();
  }
});